import { Api } from '@wellstone-solutions/common';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { fetchMe } from '@modules/auth/queries/fetch-me';
import {
  fetchDiscourseAuth,
  fetchDiscourseUser,
} from '@modules/auth/queries/use-get-discourse-user';
import { useAuthStore, useDiscourseAuthTokens } from '@modules/auth/store';

import type { AxiosResponse } from 'axios';

type DecodedToken = {
  userId: string;
};

export type AuthSuccessData = {
  access: string;
  refresh: string;
};

export const useAuthenticationFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sso, sig, clearDiscourseAuthTokens } = useDiscourseAuthTokens();

  const handleTokenAuthentication = async (access: string, refresh: string) => {
    const decoded = jwtDecode<DecodedToken>(access);
    if (!decoded.userId) throw new Error('Missing userId in token');

    const store = useAuthStore.getState();
    store.setTokens(access, refresh);
    store.setUserLoggedIn();

    await Api.Instance.v2().setAuthTokens({ access, refresh });

    return { userId: decoded.userId, store };
  };

  const handleDiscourseUser = async () => {
    const store = useAuthStore.getState();

    try {
      // First check if we have a screen name in the store
      if (store.userInfo?.screenName) {
        if (sso && sig) {
          try {
            const response = await fetchDiscourseAuth(
              sso,
              sig,
              store.userInfo.screenName
            );

            // clear the auth tokens before redirecting
            clearDiscourseAuthTokens();
            return { redirectUrl: response.data.redirection };
          } catch (error) {
            console.error('Error redirecting to Discourse:', error);
          }
        }

        console.error('Invalid or missing Recovery Collective auth tokens');

        return { redirectPath: '/auth/create-username' };
      }

      // If no screen name in store, try to fetch from Discourse
      const discourseUser = await fetchDiscourseUser();

      if (discourseUser?.username) {
        store.setScreenName(discourseUser.username);

        if (sso && sig) {
          try {
            const response = await fetchDiscourseAuth(
              sso,
              sig,
              discourseUser.username
            );

            clearDiscourseAuthTokens();
            return { redirectUrl: response.data.redirection };
          } catch (error) {
            console.error('Error redirecting to Discourse:', error);
          }
        } else {
          return { redirectPath: '/auth/create-username' };
        }
      }
      return { redirectPath: '/auth/create-username' };
    } catch (error) {
      console.error('Error fetching discourse user:', error);
      return { redirectPath: '/auth/create-username' };
    }
  };

  const processLoginSuccess = async (data: AxiosResponse<AuthSuccessData>) => {
    try {
      const { access, refresh } = data.data;

      // Handle token authentication
      const { store } = await handleTokenAuthentication(access, refresh);

      // Fetch and set user data
      const user = await fetchMe(access);
      store.setAuthUser(user);

      if (!user.id) throw new Error('Missing user id');

      // Process Discourse user and handle redirection
      const discourseResult = await handleDiscourseUser();

      if (discourseResult.redirectUrl) {
        window.location.href = discourseResult.redirectUrl;
        return {
          success: true,
          message: t('auth.login.success_message'),
          redirected: true,
        };
      } else if (discourseResult.redirectPath) {
        navigate(discourseResult.redirectPath);
      }

      return {
        success: true,
        message: t('auth.login.success_message'),
        user,
      };
    } catch (err) {
      console.error('Login failed post-processing:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  };

  return {
    processLoginSuccess,
    handleTokenAuthentication,
    handleDiscourseUser,
  };
};
